import { Checkbox } from '@components/Checkbox/Checkbox';
import { useResponsiveColumnWidths } from '@components/Table/hooks/useResponsiveColumnWidths';
import { TableHeader } from '@components/Table/TableHeader/TableHeader';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, useWindowDimensions } from 'react-native';
import { SizableText } from 'tamagui';
import {
  defaultEmptyMessage,
  defaultPadding,
  usedSelectionWidth,
} from './table.constants';
import { StyledTable } from './table.styled';

export interface TableProps<T> {
  data: T[];
  idKey: keyof T;

  columnDefs: ColumnDef<T>[];

  title?: string;
  emptyMessage?: string;

  selectable?: boolean;
  selectedRowIds?: string[];
  onSelectionChange?: (selectedRows: T[]) => void;

  onSortingChange?: (sorted: SortingState) => void;
  /** Whether you can sort by multiple columns. Default to TRUE. */
  multiSort?: boolean;
  onRowPress?: (row: T) => void;
}

/**
 * Table component built with Tamagui and TanStack React Table
 * Displays data in a tabular format with header and rows
 * Supports empty states, custom cell rendering, and horizontal scrolling
 * Features responsive column sizing with 20% deviation constraint and row selection
 */
export const Table = <T,>({
  data,
  idKey,
  columnDefs,
  emptyMessage = defaultEmptyMessage,
  selectable = false,
  selectedRowIds,
  onSelectionChange,
  onSortingChange,
  multiSort = true,
  onRowPress,
}: TableProps<T>): React.ReactNode => {
  const { width: screenWidth } = useWindowDimensions();

  // Row selection state
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([]);

  // Calculate available width for columns
  const availableWidth = useMemo(() => {
    const selectionWidth = selectable ? usedSelectionWidth : 0;
    return screenWidth - defaultPadding - selectionWidth;
  }, [screenWidth, selectable]);

  // Make columns responsive
  const responsiveColumns = useResponsiveColumnWidths(
    columnDefs,
    availableWidth,
  );

  const allRowsSelected = useMemo(() => {
    if (!selectable || !selectedRowIds || data.length === 0) {
      return false;
    }

    const selectedIdSet = new Set(selectedRowIds);
    return data.every((row) => selectedIdSet.has(String(row[idKey])));
  }, [data, idKey, selectable, selectedRowIds]);

  // Create selection column if selectable
  const columnsWithSelection = useMemo(() => {
    if (!selectable) return responsiveColumns;

    const selectionColumn: ColumnDef<T> = {
      id: 'select',
      header: ({ table }) => {
        const isAllSelected = selectedRowIds
          ? allRowsSelected
          : table.getIsAllRowsSelected();

        return (
          <Checkbox
            checked={isAllSelected}
            onCheckedChange={(checked) => table.toggleAllRowsSelected(checked)}
            ariaLabel="Select all rows"
          />
        );
      },
      cell: ({ row }) => (
        <Pressable onPress={() => null}>
          {/* Pressable is used to remove the row press action on the cell */}
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(checked) => row.toggleSelected(checked)}
            ariaLabel={`Select row ${row.index}`}
          />
        </Pressable>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      size: 50,
    };

    return [selectionColumn, ...responsiveColumns];
  }, [allRowsSelected, responsiveColumns, selectable, selectedRowIds]);

  // Creating our table instance
  const table = useReactTable({
    data,
    columns: columnsWithSelection,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: selectable,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => String(row[idKey]),
    manualSorting: true,
    state: {
      rowSelection,
      sorting,
    },
    onSortingChange: setSorting,
    isMultiSortEvent: () => multiSort,
  });

  // Handle selection changes
  useEffect(() => {
    if (selectable && onSelectionChange) {
      const selectedRows = table
        .getFilteredSelectedRowModel()
        .rows.map((row) => row.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection, selectable, onSelectionChange, table]);

  // Handle sorting changes
  useEffect(() => {
    if (onSortingChange) {
      onSortingChange(sorting);
    }
  }, [sorting, onSortingChange]);

  // Render empty state
  const renderEmpty = useCallback(
    (): React.ReactNode => (
      <StyledTable.Row>
        <StyledTable.Cell colSpan={999}>
          <SizableText fontSize="$2" color="$color" textAlign="center">
            {emptyMessage}
          </SizableText>
        </StyledTable.Cell>
      </StyledTable.Row>
    ),
    [emptyMessage],
  );

  // Render table header
  const renderHeader = useCallback(
    (): React.ReactNode => (
      <StyledTable.Head>
        {table.getHeaderGroups().map((headerGroup) => (
          <StyledTable.Row key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHeader key={header.id} header={header} />
            ))}
          </StyledTable.Row>
        ))}
      </StyledTable.Head>
    ),
    [table],
  );

  // Render table body
  const renderBody = useCallback((): React.ReactNode => {
    if (data.length === 0) {
      return renderEmpty();
    }

    return (
      <>
        {table.getRowModel().rows.map((row) => (
          <StyledTable.Row
            key={row.id}
            selected={selectable ? row.getIsSelected() : false}
            onPress={
              onRowPress ? (): void => onRowPress(row.original) : undefined
            }
            cursor={onRowPress ? 'pointer' : undefined}
            hoverStyle={
              onRowPress ? { backgroundColor: '$tertiary50' } : undefined
            }
          >
            {row.getVisibleCells().map((cell) => (
              <StyledTable.Cell
                key={cell.id}
                align="left"
                style={{ width: cell.column.getSize() }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </StyledTable.Cell>
            ))}
          </StyledTable.Row>
        ))}
      </>
    );
  }, [data.length, renderEmpty, table, selectable, onRowPress]);

  return (
    <StyledTable>
      <StyledTable.Table>
        {renderHeader()}
        <StyledTable.Body>{renderBody()}</StyledTable.Body>
      </StyledTable.Table>
    </StyledTable>
  );
};
